import { useEffect, useState } from "react";
import { Loader2, Check, X, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CELL = { Track: "bg-slate-100 text-slate-600", Build: "bg-blue-50 text-[#1a5fd0]", Expand: "bg-[#b0862f]/10 text-[#b0862f]" };

export default function MembershipMatrix() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState("");

  const load = () => api.get("/entitlements/config").then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  useEffect(() => { load(); }, []);

  const update = async (key, patch) => {
    setSaving(key);
    try {
      await api.put("/entitlements/config", { key, ...patch });
      await load();
      toast.success("Entitlement updated");
    } catch { toast.error("Could not update"); }
    finally { setSaving(""); }
  };

  if (!cfg) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-[#1a5fd0]" /></div>;

  return (
    <div data-testid="membership-matrix">
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Membership & Entitlements</h1>
      <p className="mt-1 text-[14px] text-slate-500">Control which membership unlocks each feature. Changes take effect immediately across the member portal — no code changes required.</p>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-left text-[13px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 font-mono text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Feature</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Included from plan</th>
              <th className="px-4 py-3">Available</th>
              <th className="px-4 py-3">Enabled</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cfg.features.map((f) => (
              <tr key={f.key} data-testid={`matrix-row-${f.key}`} className="hover:bg-slate-50/50">
                <td className="px-4 py-3">
                  <div className="font-head font-bold text-slate-900">{f.name}</div>
                  <div className="font-mono text-[10px] text-slate-400">{f.key}</div>
                </td>
                <td className="px-4 py-3 text-slate-500">{f.category}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {cfg.plans.map((p) => (
                      <button key={p} onClick={() => update(f.key, { min_plan: p })} disabled={saving === f.key}
                        data-testid={`matrix-plan-${f.key}-${p}`}
                        className={`rounded-md px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider transition-all ${f.min_plan === p ? CELL[p] + " ring-1 ring-current" : "text-slate-300 hover:text-slate-500"}`}>
                        {f.min_plan === p && <Lock size={9} className="mr-1 inline" />}{p}
                      </button>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => update(f.key, { available: !f.available })} disabled={saving === f.key}
                    data-testid={`matrix-available-${f.key}`}
                    className={`inline-flex h-6 w-11 items-center rounded-full transition-colors ${f.available ? "bg-emerald-500" : "bg-slate-300"}`}>
                    <span className={`h-5 w-5 rounded-full bg-white transition-transform ${f.available ? "translate-x-5" : "translate-x-0.5"}`} />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => update(f.key, { enabled: !f.enabled })} disabled={saving === f.key}
                    data-testid={`matrix-enabled-${f.key}`}
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold ${f.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {f.enabled ? <Check size={12} /> : <X size={12} />} {f.enabled ? "On" : "Off"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[12px] text-slate-400">"Available" off shows members a Coming Soon / waitlist state. "Included from plan" sets the minimum membership that unlocks the feature.</p>
    </div>
  );
}
